import { FC, useState } from 'react';
import { Table, Drawer, Modal, Checkbox, Text, Button, Form } from 'rsuite';

import EditIcon from '@rsuite/icons/Edit';
import PlusIcon from '@rsuite/icons/Plus';
import styles from '../baseCreativesManager.module.scss';
import { ApprovalStatus, CreativeType } from 'core/creative/Creative';
import { L1ObjectChannel } from 'core/l1Object/L1Object';
import { RsButton } from 'components/Rsuite/Button';
import CreativeForm from 'components/Rsuite/CreativeForm/CreativeForm';
import BindCreativeForm from 'components/Rsuite/BindCreativeForm/BindCreativeForm';
import i18n from 'i18n';
import useCreativeStore from 'store/creativeStore';
import {
  BindingManager,
  DefaultBindingManager,
} from 'core/binding/BindingManager';
import { useCampaignStore } from 'store/campaignStore';
import { toast } from 'react-toastify';

import useCreativeForm from 'components/Rsuite/CreativeForm/Hooks/useCreativeForm';
import { ProductAvailability } from 'core/product/Product';
import { RsTag } from 'components/Rsuite/Tag/Tag';
import { RsCreativeBindingStateTag } from 'components/Rsuite/Tag';
import { get } from 'lodash';
interface BaseCreativeManagerProps {
  campaignId: string;
}
const bindingManager: BindingManager = new DefaultBindingManager();
const { Column, HeaderCell, Cell } = Table;

const CheckCell = ({
  rowData,
  onChange,
  selectCreatives,
  dataKey,
  ...props
}) => (
  <Cell {...props} style={{ padding: 0 }}>
    <div style={{ lineHeight: '46px' }}>
      <Checkbox
        value={rowData[dataKey]}
        onChange={onChange}
        checked={selectCreatives.some((item) => item === rowData[dataKey])}
      />
    </div>
  </Cell>
);

export const BaseCreativeManager: FC<BaseCreativeManagerProps> = ({
  campaignId,
}) => {
  const [selectCreatives, setSelectCreatives] = useState<any[]>([]);
  const [modelOpen, setModelOpen] = useState(false);
  const [active, setActive] = useState<boolean>(false);
  const { handleLoading } = useCampaignStore();

  const {
    actionType,
    creatives,
    bindCreatives,
    creativesPermission,
    showCreativeManager,
    showBindCreativeManager,
    openCreativeManager,
    openBindCreativeManager,
    handleChangeActionType,
    handleAddBindCreatives,
  } = useCreativeStore();

  const { getCreativesByCampaignId } = useCreativeForm();
  let checked = false;
  let indeterminate = false;

  if (selectCreatives.length === creatives.length) {
    checked = true;
  } else if (selectCreatives.length === 0) {
    checked = false;
  } else if (
    selectCreatives.length > 0 &&
    selectCreatives.length < creatives.length
  ) {
    indeterminate = true;
  }

  const handleCheckAll = (value, checked) => {
    const keys = checked ? creatives.map((item) => item.l3ChannelId) : [];
    setSelectCreatives(keys);
  };
  const handleCheck = (value, checked) => {
    const keys = checked
      ? [...selectCreatives, value.toString()]
      : selectCreatives.filter((item) => item !== value);
    setSelectCreatives(keys);
  };
  const handleCreateCreative = () => {
    handleChangeActionType('create');
    openCreativeManager(true);
  };
  const handleEditCreative = (creative) => {
    handleChangeActionType('edit');
    useCreativeStore.setState({ creative: creative });
    openCreativeManager(true);
  };
  const handleOpenBindCreative = () => {
    handleChangeActionType('bind');
    openBindCreativeManager(true);
  };
  const handleCloseBindCreative = () => {
    openBindCreativeManager(false);
    handleAddBindCreatives([]);
  };

  const handleOpenStatusModal = async (status: boolean) => {
    setModelOpen(true);
    setActive(status);
  };
  const handleCloseStatusModal = () => {
    setModelOpen(false);
    setSelectCreatives([]);
  };

  const handleBindCreative = async () => {
    try {
      handleLoading(true);
      const response: any = await bindingManager.updateBinding(
        L1ObjectChannel.RTB,
        [
          {
            active: active,
            l3ChannelIds: selectCreatives,
          },
        ],
      );
      if (response && response.status === 200) {
        toast.success('更新成功');
        await getCreativesByCampaignId(campaignId);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      handleLoading(false);
      handleCloseStatusModal();
    }
  };
  const handleBindCreatives = async () => {
    handleLoading(true);
    try {
      const bind = await bindingManager.createBinding(L1ObjectChannel.RTB, [
        {
          l2ChannelIds: [campaignId],
          active: true,
          creativeIds: [...bindCreatives],
        },
      ]);
      console.log('bind', bind);
      toast.success('素材綁定成功');
      getCreativesByCampaignId(campaignId);
      openBindCreativeManager(false);
    } catch (error) {
      console.error(error);
    } finally {
      handleLoading(false);
      handleAddBindCreatives([]);
    }
  };

  return (
    <Form layout='horizontal' className='w-full'>
      <Form.Group controlId='data-range-radio'>
        <Form.ControlLabel>素材上傳</Form.ControlLabel>
        <div className='flex'>
          <div className={styles.baseCreativeManager}>
            <div className='mb-4 flex items-center justify-end gap-4'>
              <RsButton
                appearance='primary'
                disabled={selectCreatives.length === 0}
                onClick={() => handleOpenStatusModal(true)}
              >
                啟用
              </RsButton>
              <RsButton
                appearance='ghost'
                disabled={selectCreatives.length === 0}
                onClick={() => handleOpenStatusModal(false)}
              >
                停用
              </RsButton>
            </div>
            <Table
              data={creatives}
              bordered={false}
              cellBordered={false}
              hover={true}
              showHeader={true}
              minHeight={100}
              maxHeight={360}
              headerHeight={40}
              autoHeight={true}
              rowHeight={80}
              renderEmpty={() => (
                <div className='flex h-full items-center justify-center bg-white text-center'>
                  <Text className='font-bold'>請新增或綁定素材</Text>
                </div>
              )}
            >
              <Column width={80} verticalAlign={'middle'} align='center' fixed>
                <HeaderCell style={{ padding: 0 }}>
                  <div style={{ lineHeight: '40px' }}>
                    <Checkbox
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </HeaderCell>
                <CheckCell
                  dataKey='l3ChannelId'
                  selectCreatives={selectCreatives}
                  onChange={handleCheck}
                  rowData={undefined}
                />
              </Column>
              <Column width={90} verticalAlign={'middle'} align='center' fixed>
                <HeaderCell>綁定狀態</HeaderCell>
                <Cell dataKey='bindingState'>
                  {(rowData) => {
                    return rowData.bindingState === 1 ? (
                      <RsTag
                        label='啟用'
                        appearance='default'
                        color='green'
                        size='md'
                      />
                    ) : (
                      <RsTag label='停用' appearance='default' size='md' />
                    );
                  }}
                </Cell>
              </Column>
              <Column
                width={90}
                verticalAlign={'middle'}
                fullText
                fixed
                align='center'
              >
                <HeaderCell>ID</HeaderCell>
                <Cell className='id' dataKey='id' />
              </Column>
              <Column width={380} fullText flexGrow={1}>
                <HeaderCell>素材名稱</HeaderCell>
                <Cell className='name' dataKey='name' />
              </Column>
              <Column
                width={220}
                verticalAlign={'middle'}
                align='center'
                fullText
              >
                <HeaderCell>素材預覽</HeaderCell>
                <Cell className='bannerImageUrl' dataKey='bannerImageUrl'>
                  {(rowData) => {
                    return (
                      <img
                        width={120}
                        height={40}
                        src={rowData.bannerImageUrl}
                        alt={rowData.creativeName}
                        className='rounded-md object-contain'
                      />
                    );
                  }}
                </Cell>
              </Column>
              <Column width={130} verticalAlign={'middle'} sortable fullText>
                <HeaderCell>素材類型</HeaderCell>
                <Cell className='creativeType' dataKey='creativeType'>
                  {(rowData) => {
                    return (
                      <>
                        {i18n.t<string>(
                          `bindCreativeStateContent.labels.${CreativeType[rowData.creativeType].toLowerCase()}`,
                        )}
                      </>
                    );
                  }}
                </Cell>
              </Column>
              {/* <Column width={140} verticalAlign={'middle'} fullText>
                <HeaderCell>主打品/商品 ID</HeaderCell>
                <Cell className='actionType' dataKey='actionType'>
                  {(rowData) => {
                    if (!rowData?.product) {
                      return `N/A`;
                    }
                    return `${rowData?.product?.productId}`;
                  }}
                </Cell>
              </Column> */}
              <Column width={90} verticalAlign={'middle'} align='center'>
                <HeaderCell>送審狀態</HeaderCell>
                <Cell className='approvalStatus' dataKey='approvalStatus'>
                  {(rowData) => {
                    let stateColor = [
                      '!text-[#615f67]', // default
                      '!text-[#ffa953]', // warning
                      '!text-[#414651]', // success
                      '!text-[#D60000]', // error
                      '!text-[#242328]', // default
                      '!text-[#ffa953]', // pending
                      '!text-[#ce555c]', // rejected
                    ];
                    if (!rowData.approvalStatus?.asiamax) {
                      return `N/A`;
                    }
                    return (
                      <RsCreativeBindingStateTag
                        label={
                          <div>
                            {i18n.t<string>(
                              `creativeReview.labels.${ApprovalStatus[rowData.approvalStatus.asiamax?.state].toLowerCase()}`,
                            )}
                          </div>
                        }
                        className={`${stateColor[rowData.approvalStatus.asiamax?.state]} border-none`}
                        size='lg'
                      />
                    );
                  }}
                </Cell>
              </Column>
              <Column
                width={90}
                verticalAlign={'middle'}
                align='center'
                fixed='right'
              >
                <HeaderCell>商品狀態</HeaderCell>
                <Cell dataKey='bannerExtra.availability'>
                  {(rowData) => {
                    const stockAvailable =
                      get(
                        rowData,
                        'bannerExtra.availability',
                        ProductAvailability.IN_STOCK,
                      ) === ProductAvailability.IN_STOCK;
                    return stockAvailable ? (
                      <RsTag
                        label='正常'
                        appearance='ghost'
                        color='green'
                        size='md'
                        className='!border-none !bg-transparent !text-[14px] !text-[#414651]'
                      />
                    ) : (
                      <RsTag
                        label='無庫存'
                        appearance='ghost'
                        size='md'
                        className='!border-none !bg-transparent !text-[14px] !text-[#414651]'
                      />
                    );
                  }}
                </Cell>
              </Column>
              <Column
                width={80}
                verticalAlign={'middle'}
                align='center'
                fixed='right'
              >
                <HeaderCell>操作</HeaderCell>
                <Cell>
                  {(rowData) => {
                    return (
                      <Button
                        appearance='subtle'
                        onClick={() => handleEditCreative(rowData)}
                        disabled={
                          rowData.creativeType === 9 ||
                          !creativesPermission[`option_${rowData.creativeType}`]
                        }
                      >
                        <EditIcon />
                      </Button>
                    );
                  }}
                </Cell>
              </Column>
            </Table>
            <div className='flex gap-4 bg-white p-3 shadow-sm'>
              <div
                className='flex items-center gap-1'
                onClick={handleCreateCreative}
              >
                <PlusIcon
                  color='#413a94'
                  style={{ fontSize: 10 }}
                  className='cursor-pointer'
                />
                <span className='cursor-pointer text-sm text-[#413a94]'>
                  新增素材
                </span>
              </div>
              <div
                className='flex items-center gap-1'
                onClick={handleOpenBindCreative}
              >
                <PlusIcon
                  color='#413a94'
                  style={{ fontSize: 10 }}
                  className='cursor-pointer'
                />
                <span className='cursor-pointer text-sm text-[#413a94]'>
                  綁定素材
                </span>
              </div>
            </div>
          </div>
        </div>
        <Drawer
          placement={'right'}
          size={actionType === 'bind' ? 'lg' : 'md'}
          open={showCreativeManager}
          onClose={() => openCreativeManager(false)}
        >
          <Drawer.Header>
            <Drawer.Title>
              {(() => {
                switch (actionType) {
                  case 'create':
                    return '新增素材';
                  case 'edit':
                    return '編輯素材';
                  default:
                    return '';
                }
              })()}
            </Drawer.Title>
          </Drawer.Header>
          <Drawer.Body>
            <CreativeForm campaignId={campaignId} />
          </Drawer.Body>
        </Drawer>
        <Drawer
          placement={'right'}
          size='lg'
          open={showBindCreativeManager}
          onClose={() => handleCloseBindCreative()}
        >
          <Drawer.Header>
            <Drawer.Title>
              {(() => {
                switch (actionType) {
                  case 'create':
                    return '新增素材';
                  case 'edit':
                    return '編輯素材';
                  case 'bind':
                    return '綁定素材';
                  default:
                    return '';
                }
              })()}
            </Drawer.Title>
          </Drawer.Header>
          <Drawer.Body className='p-0'>
            <BindCreativeForm campaignId={campaignId} />
          </Drawer.Body>
          <div className='sticky bottom-0 z-50 flex justify-start gap-4 bg-white p-4 drop-shadow-[0_2px_3px_rgba(97,95,103,0.5)]'>
            <RsButton
              appearance='primary'
              onClick={() => handleBindCreatives()}
              disabled={bindCreatives.length === 0}
            >
              確認綁定
            </RsButton>
            <RsButton
              appearance='subtle'
              onClick={() => handleCloseBindCreative()}
            >
              取消
            </RsButton>
          </div>
        </Drawer>
        <Modal
          backdrop={true}
          keyboard={false}
          open={modelOpen}
          onClose={() => handleCloseStatusModal()}
        >
          <Modal.Header>
            <Modal.Title>{`確定將所有素材切為${active === true ? '啟用' : '停用'}`}</Modal.Title>
          </Modal.Header>

          <Modal.Body></Modal.Body>
          <Modal.Footer className='flex justify-end'>
            <RsButton appearance='primary' onClick={() => handleBindCreative()}>
              確認
            </RsButton>
            <RsButton
              appearance='subtle'
              onClick={() => handleCloseStatusModal()}
            >
              取消
            </RsButton>
          </Modal.Footer>
        </Modal>
      </Form.Group>
    </Form>
  );
};
